// src/modules/quantum/QubitLab.jsx
// Qubit Lab — superposition · Bloch sphere · measurement · decoherence · multi-qubit

import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = '#a78bfa';
const P  = ['#a78bfa','#22d3ee','#4ade80','#fbbf24','#f87171','#fb923c'];

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=0.01,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);

// ─── MATH ─────────────────────────────────────────────────────────────────────
const cos = Math.cos, sin = Math.sin, sqrt = Math.sqrt, PI = Math.PI;

// Qubit state |ψ⟩ = cos(θ/2)|0⟩ + e^(iφ)sin(θ/2)|1⟩
const qubitState = (theta, phi) => ({
  alpha: cos(theta/2),                                    // amplitude of |0⟩
  beta:  { re: cos(phi)*sin(theta/2), im: sin(phi)*sin(theta/2) }, // amplitude of |1⟩
  prob0: cos(theta/2)**2,
  prob1: sin(theta/2)**2,
  // Bloch sphere cartesian
  x: sin(theta)*cos(phi),
  y: sin(theta)*sin(phi),
  z: cos(theta),
});

// Apply gate to (theta, phi)
const applyGate = (gate, theta, phi) => {
  switch(gate) {
    case 'X':  return { theta: PI-theta, phi: phi+PI };
    case 'Y':  return { theta: PI-theta, phi: -phi };
    case 'Z':  return { theta, phi: phi+PI };
    case 'H':  return { theta: PI/2, phi: phi };   // simplified: puts on equator
    case 'S':  return { theta, phi: phi+PI/2 };
    case 'T':  return { theta, phi: phi+PI/4 };
    case 'Rx': return { theta: theta+PI/4, phi };
    case 'Ry': return { theta: theta+PI/4, phi: phi+PI/2 };
    default:   return { theta, phi };
  }
};

// ─── BLOCH SPHERE SVG ─────────────────────────────────────────────────────────
function BlochSphere({ theta, phi, animating }) {
  const W=320, H=320, cx=160, cy=160, R=110;

  // Project 3D to 2D (simple isometric)
  const proj = (x,y,z) => ({
    px: cx + R*(x*0.8 - y*0.4),
    py: cy - R*(z*0.9 - y*0.3),
  });

  const state = qubitState(theta, phi);
  const tip = proj(state.x, state.y, state.z);

  // Equator ellipse points
  const eqPoints = Array.from({length:61},(_,i)=>{
    const a=i*PI/30;
    return proj(cos(a),sin(a),0);
  });
  const eqPath = eqPoints.map((p,i)=>`${i===0?'M':'L'}${p.px},${p.py}`).join(' ');

  // Meridian ellipse
  const merPoints = Array.from({length:61},(_,i)=>{
    const a=i*PI/30;
    return proj(cos(a),0,sin(a));
  });
  const merPath = merPoints.map((p,i)=>`${i===0?'M':'L'}${p.px},${p.py}`).join(' ');

  // Second meridian
  const mer2Points = Array.from({length:61},(_,i)=>{
    const a=i*PI/30;
    return proj(0,cos(a),sin(a));
  });
  const mer2Path = mer2Points.map((p,i)=>`${i===0?'M':'L'}${p.px},${p.py}`).join(' ');

  // State vector path
  const origin = proj(0,0,0);

  // Axes
  const xEnd = proj(1.2,0,0), yEnd = proj(0,1.2,0), zEnd = proj(0,0,1.2);
  const xn = proj(-1.2,0,0), yn = proj(0,-1.2,0), zn = proj(0,0,-1.2);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'10px',border:'1px solid #1e293b',display:'block'}}>
      {/* Sphere outline */}
      <circle cx={cx} cy={cy} r={R} fill="none" stroke="#1e293b" strokeWidth={1.5}/>
      <circle cx={cx} cy={cy} r={R} fill="#a78bfa04"/>

      {/* Grid lines */}
      <path d={eqPath}  fill="none" stroke="#1e293b" strokeWidth={1} strokeDasharray="3 2"/>
      <path d={merPath} fill="none" stroke="#1e293b" strokeWidth={1} strokeDasharray="3 2"/>
      <path d={mer2Path}fill="none" stroke="#1e293b" strokeWidth={1} strokeDasharray="3 2"/>

      {/* Axes */}
      <line x1={origin.px} y1={origin.py} x2={zEnd.px} y2={zEnd.py} stroke="#4ade80" strokeWidth={1.5}/>
      <line x1={origin.px} y1={origin.py} x2={zn.px}   y2={zn.py}   stroke="#4ade80" strokeWidth={1} strokeDasharray="4 2"/>
      <line x1={origin.px} y1={origin.py} x2={xEnd.px} y2={xEnd.py} stroke="#22d3ee" strokeWidth={1.5}/>
      <line x1={origin.px} y1={origin.py} x2={xn.px}   y2={xn.py}   stroke="#22d3ee" strokeWidth={1} strokeDasharray="4 2"/>
      <line x1={origin.px} y1={origin.py} x2={yEnd.px} y2={yEnd.py} stroke="#f87171" strokeWidth={1.5}/>
      <line x1={origin.px} y1={origin.py} x2={yn.px}   y2={yn.py}   stroke="#f87171" strokeWidth={1} strokeDasharray="4 2"/>

      {/* Axis labels */}
      <text x={zEnd.px}  y={zEnd.py-8}  textAnchor="middle" style={{fontSize:'11px',fill:'#4ade80',fontFamily:'JetBrains Mono',fontWeight:700}}>|0⟩</text>
      <text x={zn.px}    y={zn.py+14}   textAnchor="middle" style={{fontSize:'11px',fill:'#4ade80',fontFamily:'JetBrains Mono',fontWeight:700}}>|1⟩</text>
      <text x={xEnd.px+8} y={xEnd.py}   textAnchor="start"  style={{fontSize:'9px',fill:'#22d3ee',fontFamily:'JetBrains Mono'}}>|+⟩</text>
      <text x={yEnd.px}  y={yEnd.py+14} textAnchor="middle" style={{fontSize:'9px',fill:'#f87171',fontFamily:'JetBrains Mono'}}>|i⟩</text>

      {/* Dashed projection lines */}
      <line x1={tip.px} y1={tip.py} x2={origin.px} y2={origin.py} stroke="#a78bfa" strokeWidth={1} strokeDasharray="3 2" opacity={0.4}/>

      {/* State vector */}
      <line x1={origin.px} y1={origin.py} x2={tip.px} y2={tip.py}
        stroke="#a78bfa" strokeWidth={3} strokeLinecap="round"
        style={{transition:animating?'none':'x2 .3s, y2 .3s'}}/>
      <circle cx={tip.px} cy={tip.py} r={7} fill="#a78bfa"
        style={{transition:animating?'none':'cx .3s, cy .3s'}}/>
      <circle cx={tip.px} cy={tip.py} r={12} fill="none" stroke="#a78bfa" strokeWidth={1} opacity={0.4}/>

      {/* Angle labels */}
      <text x={cx+12} y={cy-8} style={{fontSize:'9px',fill:'#fbbf24',fontFamily:'JetBrains Mono'}}>θ={((theta/PI)*180).toFixed(0)}°</text>
      <text x={cx+12} y={cy+8} style={{fontSize:'9px',fill:'#fb923c',fontFamily:'JetBrains Mono'}}>φ={((phi/PI)*180).toFixed(0)}°</text>

      {/* State label */}
      <text x={cx} y={H-10} textAnchor="middle" style={{fontSize:'10px',fill:'#a78bfa',fontFamily:'JetBrains Mono',fontWeight:700}}>
        |ψ⟩ = {cos(theta/2).toFixed(2)}|0⟩ + {sin(theta/2).toFixed(2)}e^(i·{phi.toFixed(2)})|1⟩
      </text>
    </svg>
  );
}

// ─── MEASUREMENT HISTOGRAM ───────────────────────────────────────────────────
function measureQubit(theta, shots) {
  const p0 = cos(theta/2)**2;
  let c0=0, c1=0;
  for(let i=0;i<shots;i++) Math.random()<p0 ? c0++ : c1++;
  return [
    {state:'|0⟩', count:c0, theoretical:+(p0*shots).toFixed(0), prob:+(c0/shots).toFixed(3)},
    {state:'|1⟩', count:c1, theoretical:+((1-p0)*shots).toFixed(0), prob:+(c1/shots).toFixed(3)},
  ];
}

// ─── DECOHERENCE SIMULATION ───────────────────────────────────────────────────
const simDecoherence = (T2, steps=80) =>
  Array.from({length:steps},(_,i)=>{
    const t=i*(T2*3/steps);
    return {
      t:+t.toFixed(2),
      coherence: +Math.exp(-t/T2).toFixed(4),
      blochX:    +(Math.exp(-t/T2)*Math.cos(2*PI*t*0.8)).toFixed(4),
      blochY:    +(Math.exp(-t/T2)*Math.sin(2*PI*t*0.8)).toFixed(4),
    };
  });

const NOTABLE_STATES = [
  {name:'|0⟩ — ground',  theta:0,       phi:0,     desc:'North pole. Definite |0⟩. P(0)=1.'},
  {name:'|1⟩ — excited', theta:PI,      phi:0,     desc:'South pole. Definite |1⟩. P(1)=1.'},
  {name:'|+⟩ — H|0⟩',   theta:PI/2,   phi:0,     desc:'Equator +x. Equal superposition. P(0)=P(1)=0.5.'},
  {name:'|−⟩ — H|1⟩',   theta:PI/2,   phi:PI,    desc:'Equator −x. Equal superposition with phase flip.'},
  {name:'|i⟩ — Y',       theta:PI/2,   phi:PI/2,  desc:'Equator +y. S gate applied to |+⟩.'},
  {name:'|−i⟩ — −Y',     theta:PI/2,   phi:-PI/2, desc:'Equator −y. S† gate applied to |+⟩.'},
];

const TABS = ['bloch','superposition','measurement','decoherence','multi-qubit'];

export default function QubitLab() {
  const [tab, setTab] = useState('bloch');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'Qubit Lab — the quantum bit: superposition, Bloch sphere, measurement, decoherence.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const [theta, setTheta] = useState(PI/3);
  const [phi,   setPhi]   = useState(PI/4);
  const [gateHistory, setGateHistory] = useState([]);
  const [shots, setShots] = useState(200);
  const [measurements, setMeasurements] = useState(null);
  const [T2, setT2] = useState(2.0);
  const [animating, setAnimating] = useState(false);
  const animRef = useRef();

  const state = qubitState(theta, phi);

  const applyGateBtn = (gate) => {
    const next = applyGate(gate, theta, phi);
    const nt = ((next.theta % (2*PI)) + 2*PI) % (2*PI);
    const np = ((next.phi  % (2*PI)) + 2*PI) % (2*PI);
    setTheta(nt); setPhi(np);
    setGateHistory(h=>[...h.slice(-9),gate]);
    log(`Gate ${gate} applied → θ=${((nt/PI)*180).toFixed(1)}° φ=${((np/PI)*180).toFixed(1)}°`,'ok');
  };

  const resetQubit = () => { setTheta(0); setPhi(0); setGateHistory([]); log('Qubit reset to |0⟩','info'); };

  const runMeasurement = () => {
    const m = measureQubit(theta, shots);
    setMeasurements(m);
    log(`Measured ${shots} shots: |0⟩=${m[0].count} (${(m[0].prob*100).toFixed(1)}%), |1⟩=${m[1].count} (${(m[1].prob*100).toFixed(1)}%)`,'ok');
  };

  const toggleAnim = () => {
    if(animating){ clearInterval(animRef.current); setAnimating(false); return; }
    setAnimating(true);
    animRef.current = setInterval(()=>setPhi(p=>(p+0.04)%(2*PI)), 50);
  };

  useEffect(()=>()=>clearInterval(animRef.current),[]);

  const decoData = simDecoherence(T2);

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── BLOCH ── */}
      {tab==='bloch'&&(
        <div style={{display:'grid',gridTemplateColumns:'320px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <BlochSphere theta={theta} phi={phi} animating={animating}/>
            <Btn onClick={toggleAnim} color={animating?'#f87171':AC} style={{width:'100%'}}>{animating?'STOP PRECESSION':'▶ PRECESS φ'}</Btn>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="QUBIT STATE CONTROLS" accent={AC}>
              <Sl label="θ (polar angle)" value={theta} min={0} max={PI} step={0.01} onChange={setTheta} unit=" rad" color={AC}/>
              <Sl label="φ (azimuthal angle)" value={phi} min={0} max={2*PI} step={0.01} onChange={setPhi} unit=" rad" color="#fbbf24"/>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'6px',marginTop:'4px'}}>
                <Metric label="|α|² P(0)" value={state.prob0.toFixed(4)} color="#4ade80"/>
                <Metric label="|β|² P(1)" value={state.prob1.toFixed(4)} color="#f87171"/>
                <Metric label="Bloch x" value={state.x.toFixed(3)} color="#22d3ee"/>
                <Metric label="Bloch z" value={state.z.toFixed(3)} color="#a78bfa"/>
              </div>
            </Panel>
            <Panel title="APPLY QUANTUM GATES" accent="#4ade80">
              <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'10px'}}>
                {['X','Y','Z','H','S','T','Rx','Ry'].map(g=>(
                  <Btn key={g} onClick={()=>applyGateBtn(g)} color={g==='H'?'#fbbf24':g==='X'?'#f87171':g==='Y'?'#fb923c':g==='Z'?'#22d3ee':'#4ade80'} style={{padding:'6px 12px'}}>{g}</Btn>
                ))}
                <Btn onClick={resetQubit} color="#64748b">|0⟩</Btn>
              </div>
              {gateHistory.length>0&&<div style={{padding:'8px',background:'var(--deep)',borderRadius:'3px',fontFamily:'JetBrains Mono',fontSize:'10px',color:'#64748b'}}>
                |0⟩ <span style={{color:'#a78bfa'}}>→ {gateHistory.join(' → ')}</span> <span style={{color:'#4ade80'}}>→ |ψ⟩</span>
              </div>}
            </Panel>
            <Panel title="NOTABLE QUANTUM STATES" accent="#fbbf24">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'5px'}}>
                {NOTABLE_STATES.map(s=>(
                  <button key={s.name} onClick={()=>{setTheta(s.theta);setPhi(s.phi);log(`State set to ${s.name}`,'ok');}} style={{padding:'7px 10px',background:'var(--deep)',border:'1px solid var(--b0)',color:'#fbbf24',fontSize:'9px',cursor:'pointer',borderRadius:'3px',textAlign:'left',fontFamily:'JetBrains Mono'}}>
                    <div style={{fontWeight:700,marginBottom:'2px'}}>{s.name}</div>
                    <div style={{color:'var(--t3)',fontWeight:400,fontSize:'8px'}}>{s.desc}</div>
                  </button>
                ))}
              </div>
            </Panel>
            <LogPanel logs={logs} height={80}/>
          </div>
        </div>
      )}

      {/* ── SUPERPOSITION ── */}
      {tab==='superposition'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            <Panel title="WHAT IS SUPERPOSITION?" accent={AC}>
              <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.8,marginBottom:'10px'}}>
                A qubit exists in a linear combination of |0⟩ and |1⟩ simultaneously — until measured.
              </div>
              <div style={{padding:'12px',background:'var(--deep)',borderRadius:'4px',marginBottom:'10px',borderLeft:'3px solid #a78bfa'}}>
                <div style={{fontFamily:'JetBrains Mono',fontSize:'12px',color:'#a78bfa',marginBottom:'6px'}}>|ψ⟩ = α|0⟩ + β|1⟩</div>
                <div style={{fontFamily:'JetBrains Mono',fontSize:'10px',color:'#64748b'}}>|α|² + |β|² = 1</div>
                <div style={{fontFamily:'JetBrains Mono',fontSize:'10px',color:'#64748b',marginTop:'3px'}}>P(0) = |α|², P(1) = |β|²</div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                {[
                  {state:'|0⟩',a:'1',b:'0',p0:'1.0',p1:'0.0',color:'#4ade80'},
                  {state:'|1⟩',a:'0',b:'1',p0:'0.0',p1:'1.0',color:'#f87171'},
                  {state:'|+⟩',a:'1/√2',b:'1/√2',p0:'0.5',p1:'0.5',color:'#22d3ee'},
                  {state:'|−⟩',a:'1/√2',b:'-1/√2',p0:'0.5',p1:'0.5',color:'#fbbf24'},
                ].map(s=>(
                  <div key={s.state} style={{padding:'8px',background:'var(--deep)',borderRadius:'3px',borderTop:`2px solid ${s.color}`}}>
                    <div style={{fontSize:'11px',color:s.color,fontFamily:'JetBrains Mono',fontWeight:700,marginBottom:'4px'}}>{s.state}</div>
                    <div style={{fontSize:'8px',color:'var(--t3)',fontFamily:'JetBrains Mono'}}>α={s.a}, β={s.b}</div>
                    <div style={{fontSize:'8px',color:'var(--t3)',fontFamily:'JetBrains Mono'}}>P(0)={s.p0} P(1)={s.p1}</div>
                  </div>
                ))}
              </div>
            </Panel>
            <Panel title="SUPERPOSITION vs CLASSICAL" accent="#22d3ee">
              <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                {[
                  {label:'Classical bit',vals:[{v:'0',c:'#4ade80'},{v:'1',c:'#f87171'}],desc:'Either 0 OR 1. Definite state.'},
                  {label:'Qubit (|+⟩)',vals:[{v:'0 (50%)',c:'#22d3ee'},{v:'1 (50%)',c:'#22d3ee'}],desc:'Both 0 AND 1 simultaneously.'},
                ].map(({label,vals,desc})=>(
                  <div key={label} style={{padding:'12px',background:'var(--deep)',borderRadius:'4px'}}>
                    <div style={{fontSize:'10px',color:'var(--t1)',fontWeight:700,marginBottom:'6px'}}>{label}</div>
                    <div style={{display:'flex',gap:'6px',marginBottom:'6px'}}>
                      {vals.map((v,i)=>(
                        <div key={i} style={{flex:1,padding:'8px',background:v.c+'18',border:`1px solid ${v.c}44`,borderRadius:'3px',textAlign:'center',fontSize:'10px',color:v.c,fontFamily:'JetBrains Mono',fontWeight:700}}>{v.v}</div>
                      ))}
                    </div>
                    <div style={{fontSize:'9px',color:'var(--t3)'}}>{desc}</div>
                  </div>
                ))}
                <div style={{padding:'10px',background:'#a78bfa10',borderRadius:'4px',border:'1px solid #a78bfa33',fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>
                  <span style={{color:'#a78bfa',fontWeight:700}}>Key insight: </span>
                  n qubits represent 2ⁿ states simultaneously. 300 qubits → more states than atoms in the observable universe.
                </div>
              </div>
            </Panel>
          </div>
          <Panel title="PROBABILITY AMPLITUDE vs ANGLE θ" accent="#fbbf24">
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={Array.from({length:100},(_,i)=>{const t=i*PI/99;return{theta:+(t*180/PI).toFixed(1),P0:+(cos(t/2)**2).toFixed(4),P1:+(sin(t/2)**2).toFixed(4)};})}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                <XAxis dataKey="theta" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'θ (degrees)',fill:'var(--t3)',fontSize:9,position:'insideBottom',offset:-2}}/>
                <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,1]}/>
                <Tooltip/><Legend/>
                <Line type="monotone" dataKey="P0" stroke="#4ade80" strokeWidth={2} dot={false} name="P(|0⟩) = cos²(θ/2)"/>
                <Line type="monotone" dataKey="P1" stroke="#f87171" strokeWidth={2} dot={false} name="P(|1⟩) = sin²(θ/2)"/>
              </LineChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}

      {/* ── MEASUREMENT ── */}
      {tab==='measurement'&&(
        <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="MEASUREMENT CONFIG" accent="#4ade80">
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',lineHeight:1.6}}>Current state:<br/>
                <span style={{color:AC,fontFamily:'JetBrains Mono'}}>P(0) = {state.prob0.toFixed(4)}<br/>P(1) = {state.prob1.toFixed(4)}</span>
              </div>
              <Sl label="θ (state angle)" value={theta} min={0} max={PI} step={0.01} onChange={setTheta} color={AC}/>
              <Sl label="Shots (trials)" value={shots} min={10} max={2000} step={10} onChange={setShots} color="#4ade80"/>
              <Btn onClick={runMeasurement} color="#4ade80" style={{width:'100%',marginTop:'4px'}}>MEASURE →</Btn>
            </Panel>
            <Panel title="MEASUREMENT COLLAPSE" accent="#f87171">
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8}}>
                Before: |ψ⟩ = α|0⟩ + β|1⟩<br/>
                After measuring: <span style={{color:'#4ade80'}}>|0⟩</span> or <span style={{color:'#f87171'}}>|1⟩</span><br/><br/>
                <span style={{color:'#f87171',fontWeight:700}}>Collapse is irreversible.</span><br/>
                The superposition is destroyed. Each measurement is a fresh sample from P(0)=|α|², P(1)=|β|².
              </div>
            </Panel>
            <LogPanel logs={logs} height={100}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="MEASUREMENT RESULTS — BORN RULE" accent="#4ade80">
              {measurements ? (
                <>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginBottom:'10px'}}>
                    <Metric label="|0⟩ count" value={measurements[0].count} color="#4ade80"/>
                    <Metric label="|1⟩ count" value={measurements[1].count} color="#f87171"/>
                    <Metric label="P(0) measured" value={measurements[0].prob} color="#22d3ee"/>
                    <Metric label="P(0) theory" value={state.prob0.toFixed(4)} color="#a78bfa"/>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={measurements} barSize={60}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="state" tick={{fontSize:12,fill:'var(--t1)',fontFamily:'JetBrains Mono',fontWeight:700}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <Tooltip/><Legend/>
                      <Bar dataKey="count"       name="Measured"    radius={[3,3,0,0]}>
                        <Cell fill="#4ade80"/><Cell fill="#f87171"/>
                      </Bar>
                      <Bar dataKey="theoretical" name="Theoretical" radius={[3,3,0,0]} fill="#334155" opacity={0.6}/>
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{fontSize:'9px',color:'var(--t3)',marginTop:'8px',textAlign:'center'}}>
                    Statistical deviation: {Math.abs(measurements[0].prob - state.prob0).toFixed(4)} (decreases with more shots → Born Rule)
                  </div>
                </>
              ) : <div style={{height:200,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Set state angle and measure</div>}
            </Panel>
          </div>
        </div>
      )}

      {/* ── DECOHERENCE ── */}
      {tab==='decoherence'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:'12px'}}>
            <Panel title="DECOHERENCE CONFIG" accent="#fb923c">
              <Sl label="T₂ coherence time" value={T2} min={0.2} max={10} step={0.1} onChange={setT2} unit="µs" color="#fb923c"/>
              <div style={{marginTop:'8px',fontSize:'9px',color:'var(--t3)',lineHeight:1.8}}>
                <div style={{color:'#fb923c',fontWeight:700,marginBottom:'3px'}}>Real hardware T₂:</div>
                <div>Superconducting: 10-500µs</div>
                <div>Trapped ions: 1-100ms</div>
                <div>Photonic: ~ns (fast ops)</div>
                <div>Silicon spin: ~ms</div>
              </div>
            </Panel>
            <Panel title="COHERENCE DECAY — exp(−t/T₂)" accent="#fb923c">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={decoData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="t" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'time (µs)',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                  <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[-1,1]}/>
                  <Tooltip/><Legend/>
                  <Line type="monotone" dataKey="coherence" stroke="#fb923c" strokeWidth={2} dot={false} name="Coherence"/>
                  <Line type="monotone" dataKey="blochX"    stroke="#a78bfa" strokeWidth={1.5} dot={false} name="Bloch X (precession)"/>
                  <Line type="monotone" dataKey="blochY"    stroke="#22d3ee" strokeWidth={1.5} dot={false} name="Bloch Y"/>
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          </div>
          <Panel title="NOISE CHANNELS — WHAT DESTROYS QUANTUM STATES" accent="#f87171">
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
              {[
                {name:'Bit Flip (X)',formula:'ρ → (1-p)ρ + pXρX†',desc:'Random flips |0⟩↔|1⟩. Like classical bit-flip error. Probability p.',color:'#f87171'},
                {name:'Phase Flip (Z)',formula:'ρ → (1-p)ρ + pZρZ†',desc:'Flips phase of |1⟩. |+⟩→|−⟩. No classical analog. Most insidious.',color:'#fbbf24'},
                {name:'Depolarizing',formula:'ρ → (1-p)ρ + p·I/2',desc:'Replaces qubit with maximally mixed state. T1 and T2 decay combined.',color:'#fb923c'},
                {name:'Amplitude Damping',formula:'T₁ relaxation',desc:'Energy loss to environment. |1⟩→|0⟩ spontaneously. Models qubit "forgetting".',color:'#a78bfa'},
                {name:'Phase Damping',formula:'T₂ dephasing',desc:'Pure phase noise. Off-diagonal density matrix elements → 0. Kills superposition.',color:'#22d3ee'},
                {name:'Measurement Error',formula:'P(0|1), P(1|0)',desc:'Readout crosstalk. NISQ devices: 1-5% readout error. Mitigated by calibration.',color:'#4ade80'},
              ].map(({name,formula,desc,color})=>(
                <div key={name} style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',borderTop:`2px solid ${color}`}}>
                  <div style={{fontSize:'10px',color,fontWeight:700,marginBottom:'4px'}}>{name}</div>
                  <div style={{fontFamily:'JetBrains Mono',fontSize:'8px',color:color,padding:'4px 6px',background:'var(--b0)',borderRadius:'2px',marginBottom:'5px'}}>{formula}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── MULTI-QUBIT ── */}
      {tab==='multi-qubit'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            <Panel title="TENSOR PRODUCT — n QUBITS = 2ⁿ STATE SPACE" accent={AC}>
              <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                {[1,2,3,4,5,10,50,300].map(n=>{
                  const states = n<=20 ? (2**n).toLocaleString() : `2^${n}`;
                  const bar = Math.min(100, n*7);
                  return (
                    <div key={n} style={{display:'flex',alignItems:'center',gap:'8px',padding:'5px 8px',background:'var(--deep)',borderRadius:'2px'}}>
                      <span style={{fontSize:'9px',color:AC,fontFamily:'JetBrains Mono',fontWeight:700,minWidth:28}}>{n}q</span>
                      <div style={{flex:1,height:10,background:'var(--b0)',borderRadius:2,overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${bar}%`,background:`linear-gradient(90deg,${AC}44,${AC})`,borderRadius:2}}/>
                      </div>
                      <span style={{fontSize:'9px',color:'var(--t2)',fontFamily:'JetBrains Mono',minWidth:90,textAlign:'right'}}>{states} states</span>
                    </div>
                  );
                })}
              </div>
            </Panel>
            <Panel title="ENTANGLEMENT — BELL STATES" accent="#4ade80">
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.7,marginBottom:'10px'}}>
                Entangled qubits cannot be described independently — measuring one instantly determines the other, regardless of distance.
              </div>
              {[
                {name:'|Φ⁺⟩',formula:'(|00⟩ + |11⟩)/√2',desc:'Both same, random. EPR pair.'},
                {name:'|Φ⁻⟩',formula:'(|00⟩ − |11⟩)/√2',desc:'Both same with phase flip.'},
                {name:'|Ψ⁺⟩',formula:'(|01⟩ + |10⟩)/√2',desc:'Always opposite, random.'},
                {name:'|Ψ⁻⟩',formula:'(|01⟩ − |10⟩)/√2',desc:'Opposite with phase flip. Singlet state.'},
              ].map(({name,formula,desc})=>(
                <div key={name} style={{display:'flex',gap:'10px',padding:'7px 10px',background:'var(--deep)',borderRadius:'3px',marginBottom:'4px',borderLeft:'2px solid #4ade80'}}>
                  <span style={{fontSize:'11px',color:'#4ade80',fontFamily:'JetBrains Mono',fontWeight:700,minWidth:36}}>{name}</span>
                  <span style={{fontSize:'9px',color:'#22d3ee',fontFamily:'JetBrains Mono',minWidth:140}}>{formula}</span>
                  <span style={{fontSize:'9px',color:'var(--t3)'}}>{desc}</span>
                </div>
              ))}
              <div style={{marginTop:'8px',padding:'8px',background:'#4ade8010',borderRadius:'3px',border:'1px solid #4ade8030',fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>
                <span style={{color:'#4ade80',fontWeight:700}}>Circuit to create |Φ⁺⟩: </span>
                H on qubit 0, then CNOT(0→1). Output: maximally entangled Bell pair.
              </div>
            </Panel>
          </div>
          <Panel title="QUBIT vs CLASSICAL BIT — COMPLETE COMPARISON" accent="#fbbf24">
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'9px'}}>
              <thead><tr style={{background:'var(--deep)'}}>
                {['Property','Classical Bit','Qubit'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',color:'var(--t3)',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {[
                  ['State space','0 or 1','α|0⟩+β|1⟩, continuous'],
                  ['Before measurement','Definite 0 or 1','Superposition of both'],
                  ['After measurement','Same','Collapses to 0 or 1'],
                  ['n-bit state space','2ⁿ possible values (one at a time)','2ⁿ amplitudes simultaneously'],
                  ['Copying','Classical cloning always works','No-cloning theorem: cannot copy arbitrary qubit'],
                  ['Noise sensitivity','Robust','Extremely fragile — decoherence'],
                  ['Operations','Boolean logic gates','Unitary rotations on Bloch sphere'],
                ].map((row,i)=>(
                  <tr key={row[0]} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'var(--deep)':'transparent'}}>
                    <td style={{padding:'6px 10px',color:'var(--t3)',fontWeight:700}}>{row[0]}</td>
                    <td style={{padding:'6px 10px',color:'#64748b',fontFamily:'JetBrains Mono',fontSize:'9px'}}>{row[1]}</td>
                    <td style={{padding:'6px 10px',color:'#a78bfa',fontFamily:'JetBrains Mono',fontSize:'9px'}}>{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
